import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Alert,
    CircularProgress,
    Chip
} from '@mui/material';
import { Email as EmailIcon, Send as SendIcon } from '@mui/icons-material';
import { SecondaryButton, PrimaryButton } from './Buttons';
import { getDetailsReportEmailRecipient, sendDetailsReportEmail } from '../../services/reports';
import { toast } from 'react-toastify';

const SendReportEmailDialog = ({
    open,
    onClose,
    reportId,
    reportTitle = 'Detailed Report',
    onSuccess,
}) => {
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [recipientInfo, setRecipientInfo] = useState(null);
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !reportId) {
            setRecipientInfo(null);
            setEmail('');
            setName('');
            setError(null);
            return;
        }

        let isMounted = true;
        const fetchRecipient = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getDetailsReportEmailRecipient(reportId);
                if (!isMounted) return;
                setRecipientInfo(data);
                setEmail(data.recipient_email || '');
                setName(data.recipient_name || '');
                if (!data.can_send && data.error) {
                    setError(data.error);
                }
            } catch (err) {
                if (!isMounted) return;
                console.error('Failed to resolve email recipient:', err);
                setError(err.response?.data?.message || err.response?.data?.error || 'Failed to resolve recipient for this report.');
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchRecipient();
        return () => {
            isMounted = false;
        };
    }, [open, reportId]);

    const handleSend = async () => {
        if (!email || !email.trim()) {
            setError('Please enter a valid recipient email address.');
            return;
        }

        setSending(true);
        setError(null);
        try {
            const res = await sendDetailsReportEmail(reportId, {
                recipient_email: email.trim(),
                recipient_name: name.trim(),
            });
            toast.success(res.message || `Report successfully sent to ${email.trim()}`);
            if (onSuccess) onSuccess(res);
            onClose();
        } catch (err) {
            console.error('Failed to send report email:', err);
            const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to send report email.';
            setError(msg);
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <EmailIcon color="primary" />
                    <Typography variant="h6">Email Inspection Report</Typography>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                        <CircularProgress size={32} />
                        <Typography sx={{ ml: 2 }} color="text.secondary">
                            Resolving customer contact details...
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {error && (
                            <Alert severity="warning" onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Report: <strong>{reportTitle}</strong>
                            </Typography>
                            {recipientInfo?.service_order_display && (
                                <Chip
                                    size="small"
                                    label={`SO: ${recipientInfo.service_order_display}`}
                                    variant="outlined"
                                    color="primary"
                                />
                            )}
                            {recipientInfo?.service_type && (
                                <Chip
                                    size="small"
                                    label={`Type: ${recipientInfo.service_type}`}
                                    variant="outlined"
                                    color="secondary"
                                />
                            )}
                            {recipientInfo?.recipient_source && (
                                <Chip
                                    size="small"
                                    label={recipientInfo.recipient_source === 'contact_person' ? 'Contact Person' : 'Company Contact'}
                                    color="default"
                                />
                            )}
                        </Box>

                        {recipientInfo?.attachment_filename && (
                            <Box sx={{ p: 1.25, bgcolor: '#f1f5f9', borderRadius: 1.5, border: '1px dashed #94a3b8' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
                                    📎 Attachment: <strong>{recipientInfo.attachment_filename}</strong>
                                </Typography>
                            </Box>
                        )}

                        <Typography variant="body2" color="text.secondary">
                            The finalized inspection report PDF will be emailed directly as an attachment to the contact person.
                        </Typography>

                        <TextField
                            label="Recipient Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            size="small"
                            disabled={sending}
                        />

                        <TextField
                            label="Recipient Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            size="small"
                            required
                            disabled={sending}
                            helperText="Email address associated with the service order contact person"
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <SecondaryButton onClick={onClose} disabled={sending}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton
                    onClick={handleSend}
                    disabled={loading || sending || !email}
                    startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                >
                    {sending ? 'Sending…' : 'Send Report'}
                </PrimaryButton>
            </DialogActions>
        </Dialog>
    );
};

export default SendReportEmailDialog;
